import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Check, ChevronDown, ChevronUp, GripVertical, Pencil, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppDialog } from "@/components/common/AppDialog";
import type { MenuCategory } from "@/types/menu-category";
import { CATEGORY_NAME_MAX } from "./AddCategoryDialog";

interface ConfigureCategoriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The concept's categories in their saved order. */
  categories: MenuCategory[];
  /** Saves the new name. Rejects when it could not, which keeps the box open. */
  onRename: (id: number, name: string) => Promise<unknown>;
  /** Saves the order, as category ids. */
  onReorder: (orderedIds: number[]) => void;
  /** Asks to delete; the caller decides where the category's items go. */
  onDelete: (category: MenuCategory) => void;
  onAddCategory: () => void;
}

/**
 * Rename, reorder and delete the selected concept's categories. Every change is
 * saved as it is made, so the list behind the dialog and the chip bar show it
 * straight away; there is no Save to forget.
 */
export function ConfigureCategoriesDialog({
  open,
  onOpenChange,
  categories,
  onRename,
  onReorder,
  onDelete,
  onAddCategory,
}: ConfigureCategoriesDialogProps) {
  const { t } = useLingui();

  // The order while a change is being saved or dragged. Tied to the list it was
  // made from, so a fresh list from the server replaces it without an effect.
  const [override, setOverride] = useState<{ base: MenuCategory[]; ids: number[] } | null>(null);
  const ids = override?.base === categories ? override.ids : categories.map((c) => c.id);
  const byId = new Map(categories.map((c) => [c.id, c]));
  const ordered = ids.flatMap((id) => byId.get(id) ?? []);

  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState("");
  const [renameError, setRenameError] = useState<string | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);

  const reorder = (next: number[], save: boolean) => {
    setOverride({ base: categories, ids: next });
    if (save) onReorder(next);
  };

  const move = (from: number, to: number) => {
    if (to < 0 || to >= ids.length) return;
    const next = [...ids];
    const [moved] = next.splice(from, 1);
    if (moved === undefined) return;
    next.splice(to, 0, moved);
    reorder(next, true);
  };

  const startEdit = (category: MenuCategory) => {
    setEditingId(category.id);
    setDraft(category.name);
    setRenameError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setRenameError(null);
  };

  const commitEdit = async (category: MenuCategory) => {
    const name = draft.trim();
    if (!name) {
      setRenameError(t`Category name is required`);
      return;
    }
    if (name.length > CATEGORY_NAME_MAX) {
      setRenameError(t`Category name must be ${CATEGORY_NAME_MAX} characters or fewer`);
      return;
    }
    if (name === category.name) {
      cancelEdit();
      return;
    }
    setIsRenaming(true);
    try {
      await onRename(category.id, name);
      cancelEdit();
    } catch (error) {
      // The caller has already said why (a taken name, usually); stay here so
      // the name can be fixed.
      setRenameError(error instanceof Error && error.message ? error.message : t`Failed to rename category`);
    } finally {
      setIsRenaming(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) cancelEdit();
        onOpenChange(next);
      }}
      title={t`Configure categories`}
      description={t`Reorder, rename or delete the categories of this concept.`}
      className="max-w-175"
      footer={
        <div className="flex w-full justify-end gap-3">
          <Button onClick={() => onOpenChange(false)} className="gap-1.5">
            <Check className="size-4" />
            {t`Done`}
          </Button>
        </div>
      }
    >
      <div className="space-y-2">
        {ordered.length === 0 && (
          <p className="py-4 text-center text-sm text-(--color-text-secondary)">
            {t`This concept has no categories yet.`}
          </p>
        )}
        {ordered.map((category, index) => (
          <div
            key={category.id}
            draggable={editingId !== category.id}
            onDragStart={() => setDragIndex(index)}
            onDragOver={(e) => {
              e.preventDefault();
              if (dragIndex === null || dragIndex === index) return;
              const next = [...ids];
              const [moved] = next.splice(dragIndex, 1);
              if (moved === undefined) return;
              next.splice(index, 0, moved);
              reorder(next, false);
              setDragIndex(index);
            }}
            onDragEnd={() => {
              setDragIndex(null);
              if (override && override.base === categories) onReorder(override.ids);
            }}
            className={`flex items-center justify-between gap-3 rounded-lg border bg-(--color-surface-content) px-4 py-3 transition-shadow ${
              dragIndex === index ? "shadow-md ring-2 ring-primary/20" : ""
            }`}
          >
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <GripVertical className="size-4 shrink-0 cursor-grab text-(--color-text-secondary)" />
              {editingId === category.id ? (
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <Input
                    autoFocus
                    aria-label={t`Category name`}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void commitEdit(category);
                      }
                      if (e.key === "Escape") {
                        e.stopPropagation();
                        cancelEdit();
                      }
                    }}
                  />
                  {renameError && (
                    <p className="text-xs text-(--color-danger-text)">{renameError}</p>
                  )}
                </div>
              ) : (
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-medium">{category.name}</span>
                  <span className="text-xs text-(--color-text-secondary)">
                    {category.itemCount === 1 ? t`1 menu item` : t`${category.itemCount} menu items`}
                  </span>
                </div>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-1">
              {editingId === category.id ? (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 bg-(--color-surface-chip-inactive)"
                    aria-label={t`Save name`}
                    disabled={isRenaming}
                    onClick={() => void commitEdit(category)}
                  >
                    <Check className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 bg-(--color-surface-chip-inactive)"
                    aria-label={t`Cancel rename`}
                    onClick={cancelEdit}
                  >
                    <X className="size-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 bg-(--color-surface-chip-inactive)"
                    aria-label={t`Move ${category.name} up`}
                    disabled={index === 0}
                    onClick={() => move(index, index - 1)}
                  >
                    <ChevronUp className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 bg-(--color-surface-chip-inactive)"
                    aria-label={t`Move ${category.name} down`}
                    disabled={index === ordered.length - 1}
                    onClick={() => move(index, index + 1)}
                  >
                    <ChevronDown className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 bg-(--color-surface-chip-inactive)"
                    aria-label={t`Rename ${category.name}`}
                    onClick={() => startEdit(category)}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8 bg-(--color-danger) text-(--color-danger-fg) hover:bg-(--color-danger)/90"
                    aria-label={t`Delete ${category.name}`}
                    onClick={() => onDelete(category)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <Button
        variant="ghost"
        onClick={onAddCategory}
        className="mt-3 h-auto gap-2 px-0 py-0 text-sm text-(--color-text-secondary) hover:bg-transparent hover:text-(--color-text-primary)"
      >
        <Plus className="size-4" />
        {t`Add another category`}
      </Button>
    </AppDialog>
  );
}
